const LOGIN_INTENTS = new Set(["client", "photographer", "guest"]);

function trimTrailingSlash(value) {
  return String(value || "").replace(/\/+$/, "");
}

function localPanelOrigin(location, port) {
  const protocol = location?.protocol || "http:";
  const hostname = location?.hostname || "localhost";
  return `${protocol}//${hostname}:${port}`;
}

function productionPanelOrigin(location, subdomain) {
  const protocol = location?.protocol || "https:";
  const rawHostname = String(location?.hostname || "");
  if (!rawHostname) return "";

  // The Cloudflare Pages projects are deployed as sibling projects such as
  // photox-guest.pages.dev and photox-photographer.pages.dev.
  const pagesProject = rawHostname.match(/^(.+)-guest\.pages\.dev$/);
  if (pagesProject) {
    return `${protocol}//${pagesProject[1]}-${subdomain}.pages.dev`;
  }
  if (rawHostname.endsWith(".pages.dev")) return "";

  const hostname = rawHostname.replace(/^(?:www|guest)\./, "");
  return `${protocol}//${subdomain}.${hostname}`;
}

/** Canonical production deployment contract: the Guest/public app owns the apex. */
export function getProductionDomainMap(apexDomain, protocol = "https:") {
  const apex = String(apexDomain || "")
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "")
    .replace(/^(?:www|guest)\./, "");
  if (!apex || apex.includes("/") || apex.includes(":")) return null;
  return {
    guest: `${protocol}//${apex}`,
    client: `${protocol}//client.${apex}`,
    photographer: `${protocol}//photographer.${apex}`,
    admin: `${protocol}//admin.${apex}`,
  };
}

/** Read a presentation hint only. The backend remains authoritative for access. */
export function getLoginIntent(location = globalThis.location) {
  const intent = new URLSearchParams(location?.search || "").get("as") || "";
  return LOGIN_INTENTS.has(intent) ? intent : "";
}

export function loginHref(intent) {
  return LOGIN_INTENTS.has(intent) ? `/login?as=${intent}` : "/login";
}

/** Carry a short-lived, one-use backend handoff in the URL fragment. */
export function withAuthHandoff(url, handoff, workspace) {
  if (
    !url ||
    workspace === "guest" ||
    !handoff?.token ||
    !Array.isArray(handoff.workspaces) ||
    !handoff.workspaces.includes(workspace)
  ) {
    return url;
  }
  const destination = new URL(url, globalThis.location?.origin || "http://localhost");
  const fragment = new URLSearchParams(destination.hash.replace(/^#/, ""));
  fragment.set("auth_handoff", handoff.token);
  fragment.set("workspace", workspace);
  destination.hash = fragment.toString();
  return destination.toString();
}

/** Open the dedicated partner onboarding flow, not the shared Guest login. */
export function photographerApplicationHref(options = {}) {
  const urls =
    options.urls || getPanelUrls(options.location, options.environment);
  return urls.photographer
    ? `${trimTrailingSlash(urls.photographer)}/apply`
    : loginHref("photographer");
}

/**
 * Resolve panel URLs without requiring local .env changes. Deployments can set
 * the REACT_APP_*_PANEL_URL values when their production hostnames differ from
 * the conventional client/photographer/admin subdomains.
 */
export function getPanelUrls(
  location = globalThis.location,
  environment = typeof process !== "undefined" ? process.env : {},
) {
  const hostname = String(location?.hostname || "");
  const isLocal = hostname === "localhost" || hostname === "127.0.0.1";
  const currentOrigin = trimTrailingSlash(location?.origin);

  return {
    client: trimTrailingSlash(
      environment.REACT_APP_CLIENT_PANEL_URL ||
        (isLocal
          ? localPanelOrigin(location, 3000)
          : productionPanelOrigin(location, "client")),
    ),
    photographer: trimTrailingSlash(
      environment.REACT_APP_PHOTOGRAPHER_PANEL_URL ||
        (isLocal
          ? localPanelOrigin(location, 3002)
          : productionPanelOrigin(location, "photographer")),
    ),
    guest: trimTrailingSlash(
      environment.REACT_APP_GUEST_PANEL_URL || currentOrigin,
    ),
    admin: trimTrailingSlash(
      environment.REACT_APP_ADMIN_PANEL_URL ||
        (isLocal
          ? localPanelOrigin(location, 3003)
          : productionPanelOrigin(location, "admin")),
    ),
  };
}

export function getWorkspaceOptions(user, options = {}) {
  const roles = new Set(Array.isArray(user?.roles) ? user.roles : []);
  const urls =
    options.urls || getPanelUrls(options.location, options.environment);

  if (roles.has("admin")) {
    return [{ id: "admin", label: "Admin portal", url: urls.admin }];
  }

  const workspaces = [];
  if (roles.has("client")) {
    workspaces.push({
      id: "client",
      label: "Client workspace",
      url: urls.client,
    });
  }
  if (roles.has("photographer")) {
    workspaces.push({
      id: "photographer",
      label: "Photographer workspace",
      url: urls.photographer,
    });
  }
  if (roles.has("guest")) {
    workspaces.push({
      id: "guest",
      label: "Guest gallery & face search",
      url: `${urls.guest}/guest?view=gallery`,
    });
  }
  return workspaces.filter((workspace) => Boolean(workspace.url));
}

/** Convert the backend-owned routing decision into configured frontend URLs. */
export function resolveServerPostLogin(routing, options = {}) {
  const urls =
    options.urls || getPanelUrls(options.location, options.environment);
  const kind = String(routing?.kind || "");
  const ids = Array.isArray(routing?.workspaces)
    ? routing.workspaces.filter((id) =>
        ["client", "photographer", "guest"].includes(id),
      )
    : [];
  const workspaces = getWorkspaceOptions({ roles: ids }, { ...options, urls });

  if (kind === "admin") {
    return {
      kind: "admin",
      message:
        routing?.message ||
        "Administrator accounts must continue through the separate Admin portal.",
      options: [{ id: "admin", label: "Admin portal", url: urls.admin }],
    };
  }
  if (
    kind === "redirect" &&
    ["client", "photographer", "guest"].includes(routing?.destination)
  ) {
    const destination =
      workspaces.find(({ id }) => id === routing.destination) ||
      (routing.destination === "photographer"
        ? {
            id: "photographer",
            label: "Photographer workspace",
            url: urls.photographer,
          }
        : null);
    if (destination) return { kind, destination };
  }
  if (kind === "choose" && workspaces.length > 1)
    return { kind, options: workspaces };
  if (kind === "no-invitation") {
    return {
      kind,
      message:
        routing?.message ||
        "No event invitation or PhotoX workspace is linked to this account.",
      options: workspaces,
    };
  }
  return {
    kind: "unavailable",
    message:
      "PhotoX could not validate a safe workspace destination for this account.",
    options: workspaces,
  };
}

/**
 * Select the safest post-OTP destination. A Photographer intent is also valid
 * for a verified non-Admin account that still needs to submit an application.
 */
export function resolvePostLogin(user, intent = "", options = {}) {
  const roles = new Set(Array.isArray(user?.roles) ? user.roles : []);
  const urls =
    options.urls || getPanelUrls(options.location, options.environment);
  const workspaces = getWorkspaceOptions(user, { ...options, urls });

  if (roles.has("admin")) {
    return {
      kind: "admin",
      message:
        "Administrator accounts must continue through the separate Admin portal.",
      options: workspaces,
    };
  }

  if (intent === "photographer") {
    return {
      kind: "redirect",
      destination: { id: intent, url: urls.photographer },
    };
  }

  if (intent) {
    const requested = workspaces.find((workspace) => workspace.id === intent);
    if (requested) return { kind: "redirect", destination: requested };
    return {
      kind:
        intent === "client" || intent === "guest"
          ? "no-invitation"
          : "unavailable",
      message:
        intent === "client"
          ? "This verified email does not have Client access yet. Use the email from your event invitation."
          : intent === "guest"
            ? "No Guest access is linked to this email yet. Scan the event QR to continue."
            : "This account does not have access to the requested workspace.",
      options: workspaces,
    };
  }

  const professional = workspaces.filter(
    ({ id }) => id === "client" || id === "photographer",
  );
  if (professional.length > 1) return { kind: "choose", options: workspaces };
  if (professional.length === 1)
    return { kind: "redirect", destination: professional[0] };

  const guest = workspaces.find(({ id }) => id === "guest");
  if (guest) return { kind: "redirect", destination: guest };
  return {
    kind: "no-invitation",
    message:
      "No event invitation or PhotoX workspace is currently linked to this account.",
    options: [],
  };
}
